import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  message,
  Row,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import moment from "moment";
import { EllipsisOutlined, InfoCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "../../client";
import {
  ApiPaths,
  Account,
  Connection,
} from "@/providers/smarta-banking/banking-api-client";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { Label, SubValue, Td, Tr, Value } from "../../../components/styles";
import { formatAmountCurrency } from "@/v1/utils";
import Copyable from "@/v1/components/common/Copyable";
import { foundationColors } from "@/v1/themes/colors";

const getTexts = (t: TFunction) => ({
  labels: {
    untitledAccount: t("accountRow.labels.untitledAccount"),
    details: t("accountRow.labels.details"),
    accountHolderName: t("accountRow.labels.accountHolderName"),
    balance: t("accountRow.labels.balance"),
    iban: t("accountRow.labels.iban"),
    accountNumber: t("accountRow.labels.accountNumber"),
    lastSyncedAt: t("accountRow.labels.lastSyncedAt"),
    lastTransactionsAddedAt: t("accountRow.labels.lastTransactionsAddedAt"),
    connectionArchived: t("accountRow.labels.connectionArchived"),
    connectionExpired: t("accountRow.labels.connectionExpired"),
    active: t("accountRow.labels.active"),
    paused: t("accountRow.labels.paused"),
    accountActions: t("accountRow.labels.accountActions"),
  },
  actions: {
    pause: t("accountRow.actions.pause"),
    pauseDescription: t("accountRow.actions.pauseDescription"),
    resume: t("accountRow.actions.resume"),
    resumeDescription: t("accountRow.actions.resumeDescription"),
    markLowActivity: t("accountRow.actions.markLowActivity"),
    markLowActivityDescription: t(
      "accountRow.actions.markLowActivityDescription"
    ),
    unmarkLowActivity: t("accountRow.actions.unmarkLowActivity"),
    unmarkLowActivityDescription: t(
      "accountRow.actions.unmarkLowActivityDescription"
    ),
  },
  messages: {
    pauseSuccess: t("accountRow.messages.pauseSuccess"),
    pauseError: t("accountRow.messages.pauseError"),
    resumeSuccess: t("accountRow.messages.resumeSuccess"),
    resumeError: t("accountRow.messages.resumeError"),
    markLowActivitySuccess: t("accountRow.messages.markLowActivitySuccess"),
    unmarkLowActivitySuccess: t("accountRow.messages.unmarkLowActivitySuccess"),
    labelError: t("accountRow.messages.labelError"),
  },
});

type Props = {
  customerId: string;
  account: Account;
  connection: Pick<Connection, "archivedAt" | "expiredAt">;
};

export const AccountRow: React.FC<Props> = ({
  customerId,
  account,
  connection,
}) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const queryClient = useQueryClient();
  const [visibleLabel, setVisibleLabel] = useState<Account["label"]>(
    account.label
  );

  useEffect(() => {
    setVisibleLabel(account.label);
  }, [account.label]);

  const accountsQueryKey = [
    "get",
    ApiPaths.getApiCustomersByCustomerIdAccounts,
    { params: { path: { customerId } } },
  ] as const;

  const { data: accountDetails, isLoading } = api.useQuery(
    "get",
    ApiPaths.getApiCustomersByCustomerIdAccountsByAccountId,
    {
      params: { path: { customerId, accountId: account.id } },
    }
  );

  const invalidateAccounts = () =>
    queryClient.invalidateQueries({
      queryKey: ["get", ApiPaths.getApiCustomersByCustomerIdAccounts],
    });

  const invalidateExactAccounts = () =>
    queryClient.invalidateQueries({
      queryKey: accountsQueryKey,
      exact: true,
    });

  const { mutate: pauseAccount, isPending: isPausing } = api.useMutation(
    "post",
    ApiPaths.postApiCustomersByCustomerIdAccountsByAccountIdPause,
    {
      onSuccess: () => {
        message.success(texts.messages.pauseSuccess);
        invalidateAccounts();
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.pauseError);
      },
    }
  );

  const { mutate: resumeAccount, isPending: isResuming } = api.useMutation(
    "post",
    ApiPaths.postApiCustomersByCustomerIdAccountsByAccountIdResume,
    {
      onSuccess: () => {
        message.success(texts.messages.resumeSuccess);
        invalidateAccounts();
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.resumeError);
      },
    }
  );

  const { mutate: updateAccountLabel, isPending: isUpdatingLabel } =
    api.useMutation(
      "patch",
      ApiPaths.patchApiCustomersByCustomerIdAccountsByAccountIdLabel,
      {
        onSuccess: (response) => {
          setVisibleLabel(response.data.label);
          message.success(
            response.data.label
              ? texts.messages.markLowActivitySuccess
              : texts.messages.unmarkLowActivitySuccess
          );
          invalidateExactAccounts();
        },
        onError: (error) => {
          message.error(error.error?.message || texts.messages.labelError);
        },
      }
    );

  const handlePauseClick = () => {
    pauseAccount({ params: { path: { customerId, accountId: account.id } } });
  };

  const handleResumeClick = () => {
    resumeAccount({ params: { path: { customerId, accountId: account.id } } });
  };

  const handleLabelChange = (label: Account["label"] | undefined) => {
    updateAccountLabel({
      params: { path: { customerId, accountId: account.id } },
      body: { label: label ?? null },
    });
  };

  const menuItemLabel = (title: string, description: string) => (
    <div style={{ maxWidth: 320, whiteSpace: "normal" }}>
      <div>{title}</div>
      <Typography.Text style={{ fontSize: 12 }} type="secondary">
        {description}
      </Typography.Text>
    </div>
  );

  const accountActionsMenu: MenuProps = {
    items: [
      {
        key: account.pausedAt ? "resume" : "pause",
        disabled: isPausing || isResuming,
        label: account.pausedAt
          ? menuItemLabel(texts.actions.resume, texts.actions.resumeDescription)
          : menuItemLabel(texts.actions.pause, texts.actions.pauseDescription),
      },
      {
        key: visibleLabel ? "unmark-low-activity" : "mark-low-activity",
        disabled: isUpdatingLabel,
        label: visibleLabel
          ? menuItemLabel(
              texts.actions.unmarkLowActivity,
              texts.actions.unmarkLowActivityDescription
            )
          : menuItemLabel(
              texts.actions.markLowActivity,
              texts.actions.markLowActivityDescription
            ),
      },
    ],
    onClick: ({ key }) => {
      if (key === "pause") {
        handlePauseClick();
      } else if (key === "resume") {
        handleResumeClick();
      } else {
        handleLabelChange(
          key === "unmark-low-activity" ? null : "expected_low_activity"
        );
      }
    },
  };

  return (
    <Tr data-testid={`account-row-${account.id}`}>
      <Td>
        <Row align="middle" gutter={16} wrap={false}>
          <Col>
            <Value>{account.name || texts.labels.untitledAccount}</Value>
            {account.product && <SubValue>{account.product}</SubValue>}
          </Col>

          {(account.details || account.ownerName) && (
            <Col>
              <Tooltip
                title={
                  <div className="px-3 py-2">
                    {account.details && (
                      <>
                        <Label inverted>{texts.labels.details}</Label>
                        <Value className="mb-2">{account.details}</Value>
                      </>
                    )}
                    {account.ownerName && (
                      <>
                        <Label inverted>{texts.labels.accountHolderName}</Label>
                        <Value>{account.ownerName}</Value>
                      </>
                    )}
                  </div>
                }
              >
                <InfoCircleFilled
                  style={{
                    color: foundationColors["deep-purple-400"],
                    display: "block",
                  }}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      </Td>
      <Td>
        <Label>{texts.labels.balance}</Label>
        {isLoading ? (
          <Skeleton.Input active size="small" />
        ) : (
          <Value>
            {accountDetails?.data.latestBalanceAmount !== undefined &&
            accountDetails?.data.latestBalanceCurrency
              ? formatAmountCurrency(
                  accountDetails?.data.latestBalanceAmount / 100,
                  accountDetails?.data.latestBalanceCurrency
                )
              : "-"}
          </Value>
        )}
      </Td>
      <Td>
        <Label>
          {account.iban ? texts.labels.iban : texts.labels.accountNumber}
        </Label>
        <Value>
          {account.iban || account.accountNumber ? (
            <Copyable>{account.iban || account.accountNumber}</Copyable>
          ) : (
            "-"
          )}
        </Value>
      </Td>
      <Td>
        <Label>{texts.labels.lastSyncedAt}</Label>
        <Value>
          {moment(account.lastSyncedAt).format(DISPLAY_DATE_TIME_FORMAT)}
        </Value>
      </Td>
      <Td>
        <Label>{texts.labels.lastTransactionsAddedAt}</Label>
        <Value>
          {account.lastTransactionsAddedAt
            ? moment(account.lastTransactionsAddedAt).format(
                DISPLAY_DATE_TIME_FORMAT
              )
            : "-"}
        </Value>
      </Td>
      <Td className="text-center">
        {connection.archivedAt ? (
          <Tag>
            <Typography.Text type="secondary">
              {texts.labels.connectionArchived}
            </Typography.Text>
          </Tag>
        ) : connection.expiredAt ? (
          <Tag color="warning">{texts.labels.connectionExpired}</Tag>
        ) : account.pausedAt ? (
          <Tag color="warning">{texts.labels.paused}</Tag>
        ) : (
          <Tag color="success">{texts.labels.active}</Tag>
        )}
      </Td>
      <Td className="text-right">
        <Dropdown
          menu={accountActionsMenu}
          placement="bottomRight"
          trigger={["click"]}
        >
          <Button
            aria-label={texts.labels.accountActions}
            data-testid={`account-actions-menu-${account.id}`}
            icon={<EllipsisOutlined />}
            size="small"
            type="text"
          />
        </Dropdown>
      </Td>
    </Tr>
  );
};
